import React, { useEffect } from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
import ErrorIcon from '@atlaskit/icon/core/migration/error';
import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Skeleton from '@atlaskit/skeleton';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { useFlagsService } from '@atlassian/jira-flags';
import { FormattedMessage, useIntl } from '@atlassian/jira-intl';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import {
	FireOperationalAnalytics,
	FireUiAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useCombinedRequestTypeSecurity } from '../../../controllers/combined-request-type-security/index.tsx';
import { isMisconfigured } from '../../../controllers/issue-security/utils.tsx';
import { IssueSecurityConfig } from './issue-security-config/index.tsx';
import messages from './messages.tsx';
import { RequestTypePermissionsConfig } from './request-type-permissions-config/index.tsx';
import type { Props } from './types.tsx';

export const ConfigModal = ({
	isOpen,
	onClose,
	projectId,
	projectKey,
	requestTypeExternalId,
	isEmailRequestType,
	features,
	defaultFeature,
}: Props) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();
	const {
		principals,
		prependPrincipals,
		removePrincipal,
		issueSecurityState,
		makeIssueSecuritySelection,
		configurationFetch,
		configurationLoading,
		configurationErrors,
		applyConfiguration,
		applyConfigurationLoading,
	} = useCombinedRequestTypeSecurity(projectId, requestTypeExternalId, features);

	useEffect(() => {
		if (isOpen) {
			configurationFetch();
		}
	}, [configurationFetch, isOpen]);

	const apply = async (): Promise<void> => {
		const result = await applyConfiguration();
		if (result.isSuccess) {
			showFlag({
				type: 'success',
				title: formatMessage(messages.restrictionsApplySuccessTitle),
				description: formatMessage(messages.restrictionsApplySuccessDescription),
				messageId:
					'servicedesk-request-type-security.ui.config-button.config-modal.show-flag.success.modal.show-flag.success',
				messageType: 'transactional',
			});
			onClose(result.newRestrictionStatus, result.newIssueSecurityStatus);
		} else if (result.errorType === 'PRINCIPAL-OVER-LIMITS') {
			showFlag({
				type: 'error',
				title: formatMessage(messages.restrictionsApplyLimitedTitle),
				description: formatMessage(messages.restrictionsApplyLimitedDescription),
				messageId: `servicedesk-request-type-security.ui.config-button.config-modal.modal.showFlag.error.${result.errorType}`,
				messageType: 'transactional',
			});
		} else if (result.errorType === 'MISCONFIGURATION') {
			showFlag({
				type: 'error',
				title: formatMessage(messages.restrictionsApplyFailureTitle),
				description: formatMessage(messages.restrictionsApplyMisconfigurationDescription),
				messageId: `servicedesk-request-type-security.ui.config-button.config-modal.modal.showFlag.error.${result.errorType}`,
				messageType: 'transactional',
			});
		} else {
			showFlag({
				type: 'error',
				title: formatMessage(messages.restrictionsApplyFailureTitle),
				description: formatMessage(messages.restrictionsApplyFailureDescription),
				messageId: `servicedesk-request-type-security.ui.config-button.config-modal.modal.showFlag.error.${result.errorType}`,
				messageType: 'transactional',
			});
		}
	};

	const cancel = (): void => onClose();

	const modalHeader = configurationLoading ? (
		<Skeleton width="50%" height="25px" isShimmering />
	) : (
		<ModalTitle>
			<FormattedMessage {...messages.title} />
		</ModalTitle>
	);

	const firstError = configurationErrors[0];
	const modalBody = () => {
		if (firstError) {
			return (
				<>
					<FireOperationalAnalytics
						actionSubject="requestTypeSecurityModal"
						action="failed"
						attributes={{
							error: firstError.toString(),
							errorStack: firstError.stack,
							isClientFetchError: isClientFetchError(firstError),
						}}
					/>
					<SectionMessage title={formatMessage(messages.loadErrorTitle)} appearance="error">
						<Stack space="space.100">
							<Box>
								<Button onClick={() => configurationFetch()} appearance="link" spacing="none">
									<FormattedMessage {...messages.reloadButton} />
								</Button>
							</Box>
						</Stack>
					</SectionMessage>
				</>
			);
		}

		if (configurationLoading) {
			return (
				<Stack space="space.500">
					<Skeleton width="95%" height="25px" isShimmering />
					<Skeleton width="95%" height="25px" isShimmering />
					<Skeleton width="70%" height="25px" isShimmering />
					<Skeleton width="40%" height="25px" isShimmering />
				</Stack>
			);
		}

		const featureTabComponents = features
			.map((feature) => {
				if (feature === 'REQUEST_TYPE_PERMISSIONS') {
					return {
						title: formatMessage(messages.whoCanRaiseRequestsTabTitle),
						body: (
							<RequestTypePermissionsConfig
								principals={principals}
								prependPrincipals={prependPrincipals}
								removePrincipal={removePrincipal}
								isEmailRequestType={isEmailRequestType}
							/>
						),
						error: false,
					};
				}

				if (feature === 'ISSUE_SECURITY' && issueSecurityState) {
					return {
						title: formatMessage(
							fg('itsm-issue-renaming')
								? messages.whoCanViewIssuesTabTitleIssueTermRefresh
								: messages.whoCanViewIssuesTabTitle,
						),
						body: (
							<IssueSecurityConfig
								projectKey={projectKey}
								issueSecurityState={issueSecurityState}
								makeSelection={makeIssueSecuritySelection}
							/>
						),
						error: isMisconfigured(issueSecurityState.issueSecurityStatus),
					};
				}

				return null;
			})
			.filter(Boolean);

		let content: JSX.Element;
		if (featureTabComponents.length === 0) {
			throw Error('Invalid features. There must be at least 1 feature.');
		} else if (featureTabComponents.length === 1) {
			content = featureTabComponents[0].body;
		} else {
			content = (
				<Tabs
					id="request-type-security-config-modal"
					defaultSelected={defaultFeature ? features.indexOf(defaultFeature) : undefined}
				>
					<TabList>
						{featureTabComponents.map(({ title, error }) => (
							<Tab key={title}>
								<Inline space="space.025">
									<Box xcss={tabTextStyles}>{title}</Box>
									{error ? (
										<ErrorIcon spacing="spacious" label="" color={token('color.icon.danger')} />
									) : null}
								</Inline>
							</Tab>
						))}
					</TabList>
					{featureTabComponents.map(({ title, body }) => (
						<TabPanel key={`${title}-body`}>
							<Box xcss={tabPanelStyles}>{body}</Box>
						</TabPanel>
					))}
				</Tabs>
			);
		}

		return (
			<>
				<FireUiAnalytics
					actionSubject="modal"
					action="viewed"
					actionSubjectId="requestTypeSecurity"
				/>
				{content}
			</>
		);
	};

	const modalFooter = configurationLoading ? null : (
		<>
			<Button appearance="subtle" onClick={cancel}>
				<FormattedMessage {...messages.cancelButton} />
			</Button>
			<LoadingButton
				appearance="primary"
				onClick={apply}
				isLoading={applyConfigurationLoading}
				isDisabled={!!firstError}
			>
				<FormattedMessage {...messages.applyButton} />
			</LoadingButton>
		</>
	);

	return (
		<ModalTransition>
			{isOpen && (
				<Modal
					onClose={cancel}
					height={570}
					messageId="servicedesk-request-type-security.ui.config-button.config-modal.modal.config-modal-new"
					messageType="transactional"
				>
					<ModalHeader>{modalHeader}</ModalHeader>
					<ModalBody>{modalBody()}</ModalBody>
					<ModalFooter>{modalFooter}</ModalFooter>
				</Modal>
			)}
		</ModalTransition>
	);
};

const tabPanelStyles = xcss({ marginTop: 'space.250', width: '100%' });

const tabTextStyles = xcss({ marginTop: 'space.025' });
